import { chartingQueryKeys, invalidateChartingMetricPreferences } from '@/features/charting-next/api/chartingCache';
import { fetchChartingMetricPreferences, updateChartingMetricPreferences } from '@/features/charting-next/api/chartingMetricPreferenceRepository';
import { CHARTING_FIXED_METRIC_FIELDS, CHARTING_IDENTITY_METRIC_FIELDS, EMPTY_METRIC_VALUE, buildChartingMetricTableColumns, resolveChartingMetricValue } from '@/features/charting-next/models/chartingMetricPreferences';
import { useRemoteResource } from '@/features/charting-next/services/useRemoteResource';
import { computed, ref, watch } from '@vue/composition-api';

export function useChartingMetricPreferences({ sessionStore, selectedStock }) {
    const activeFields = ref([]);
    const isMutatingMetrics = ref(false);
    const metricMutationError = ref('');

    const metricPreferencesResource = useRemoteResource({
        key: () => chartingQueryKeys.metricPreferences(sessionStore.uid),
        loader: fetchChartingMetricPreferences,
        ttl: 60000,
        enabled: () => sessionStore.isAuthenticated
    });

    watch(
        () => metricPreferencesResource.data.value?.activeFields,
        (nextFields) => {
            if (Array.isArray(nextFields)) {
                activeFields.value = [...nextFields];
            }
        },
        { immediate: true }
    );

    const catalog = computed(() => metricPreferencesResource.data.value?.catalog || []);
    const activeFieldSet = computed(() => new Set(activeFields.value));
    const tableColumns = computed(() =>
        buildChartingMetricTableColumns({
            catalog: catalog.value,
            activeFields: activeFields.value
        })
    );

    const metrics = computed(() =>
        tableColumns.value.map((column) => {
            const value = resolveChartingMetricValue(column, selectedStock.value);

            return {
                field: column.field,
                label: column.label,
                value,
                isEmpty: value === EMPTY_METRIC_VALUE,
                removable: !CHARTING_FIXED_METRIC_FIELDS.has(column.field)
            };
        })
    );

    const availableMetrics = computed(() =>
        catalog.value
            .filter((metric) => !activeFieldSet.value.has(metric.field) && !CHARTING_IDENTITY_METRIC_FIELDS.has(metric.field) && !CHARTING_FIXED_METRIC_FIELDS.has(metric.field))
            .map((metric) => ({
                field: metric.field,
                label: metric.label,
                restricted: Boolean(metric.restricted),
                restrictionReason: metric.restrictionReason || ''
            }))
    );

    const persistMetricFields = async (nextFields) => {
        const previousFields = [...activeFields.value];

        activeFields.value = [...nextFields];
        isMutatingMetrics.value = true;
        metricMutationError.value = '';

        try {
            await updateChartingMetricPreferences(activeFields.value);
            await invalidateChartingMetricPreferences(sessionStore.uid);
        } catch (error) {
            activeFields.value = previousFields;
            metricMutationError.value = error.message || 'No se pudieron actualizar las metricas.';
            throw error;
        } finally {
            isMutatingMetrics.value = false;
        }
    };

    const addMetric = async (field) => {
        if (!field || activeFieldSet.value.has(field)) {
            return;
        }

        const selectedMetric = catalog.value.find((metric) => metric.field === field);

        if (selectedMetric?.restricted) {
            metricMutationError.value = selectedMetric.restrictionReason;
            return;
        }

        await persistMetricFields([...activeFields.value, field]);
    };

    const removeMetric = async (field) => {
        if (!field || !activeFieldSet.value.has(field) || CHARTING_FIXED_METRIC_FIELDS.has(field)) {
            return;
        }

        await persistMetricFields(activeFields.value.filter((item) => item !== field));
    };

    return {
        metricPreferencesResource,
        metrics,
        tableColumns,
        availableMetrics,
        isMutatingMetrics,
        metricMutationError,
        addMetric,
        removeMetric
    };
}
